import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberSexualFantasies.module.scss";
import { SEXUAL_FANTASIES } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Hero images */
import mrImg from "@/assets/UI/memberCreate/q11Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q11Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q11MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q11MrsM.webp";

/* Floating animations */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

function useMemberLayoutContext() {
  return useOutletContext();
}

const FANTASY_META = {
  threesome_group: "💋",
  dom_sub_play: "🕶️",
  watching_being_watched: "🪞",
  romantic_slow_passion: "🧸",
  roleplay_costume: "🎭",
  voyeur_exhibition: "🔥",
  power_teasing_surrender: "⚡",
};

function toggleChoice(list, value) {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}

export default function CreateMemberSexualFantasies() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [memberChoices, setMemberChoices] = useState([]);
  const [partnerChoices, setPartnerChoices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.sexual_fantasies.title", {
    defaultValue: "Sexual Fantasies",
  });

  const introLines = [
    t("onboarding.sexual_fantasies.intro1", {
      defaultValue: "This is about imagination, curiosity, and desire.",
    }),
    t("onboarding.sexual_fantasies.intro2", {
      defaultValue: "Pick anything that excites your mind.",
    }),
    t("onboarding.sexual_fantasies.intro3", {
      defaultValue: "You can choose more than one.",
    }),
  ];

  const partnerLabel = t("onboarding.sexual_fantasies.partner.label", {
    defaultValue: "Now pick fantasies for your partner",
  });

  const guideTitle = t("onboarding.sexual_fantasies.guide.title", {
    defaultValue: "Private & safe",
  });

  const guideBody = t("onboarding.sexual_fantasies.guide.body", {
    defaultValue:
      "These stay private and only help us understand your comfort zones.",
  });

  const options = (SEXUAL_FANTASIES || []).map((value) => ({
    value,
    emoji: FANTASY_META[value] || "✨",
    title: t(`onboarding.sexual_fantasies.options.${value}.label`, {
      defaultValue: value,
    }),
    hint: t(`onboarding.sexual_fantasies.tapToSelect.${value}`, {
      defaultValue: "Tap to add this vibe to your attraction mix",
    }),
  }));

  const submitDisabled =
    submitting || !memberChoices.length || (isCouple && !partnerChoices.length);

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info(
        t("onboarding.sexual_fantasies.toast.member_required", {
          defaultValue: "Pick at least one fantasy for yourself.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoices.length) {
      toast.info(
        t("onboarding.sexual_fantasies.toast.partner_required", {
          defaultValue: "Pick at least one fantasy for your partner.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberSexualFantasies: memberChoices,
          partnerSexualFantasies: partnerChoices,
        }
      : { memberSexualFantasies: memberChoices };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/sexual_fantasies",
        payload,
        { skipToast: true },
      );

      await refreshMe();
      navigate(data?.next || "/member/body_type", { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(
        t("onboarding.sexual_fantasies.toast.save_error", {
          defaultValue: "Could not save your fantasies.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderOptions({ name, choices, setChoices, subtitle }) {
    return (
      <div className={cls.block}>
        {subtitle && <div className={cls.blockTitle}>{subtitle}</div>}

        <fieldset className={cls.options}>
          <legend className="sr-only">{subtitle || title}</legend>

          {options.map((opt) => {
            const checked = choices.includes(opt.value);
            return (
              <label
                key={`${name}-${opt.value}`}
                className={`${cls.option} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setChoices((prev) => toggleChoice(prev, opt.value))
                  }
                />

                <span className={cls.optionBody}>
                  <span className={cls.optionEmoji}>{opt.emoji}</span>
                  <span className={cls.optionText}>
                    <strong>{opt.title}</strong>
                    <small>{opt.hint}</small>
                  </span>
                  <span className={cls.optionDot} />
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }

  return (
    <main className={cls.page}>
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            variants={floatSlow}
            initial="hidden"
            animate="show"
            alt=""
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            alt=""
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            variants={floatSlow}
            initial="hidden"
            animate="show"
            alt=""
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            alt=""
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              {introLines.map((l) => (
                <div key={l} className={cls.formSub}>
                  {l}
                </div>
              ))}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit}>
            {renderOptions({
              name: "memberSexualFantasies",
              choices: memberChoices,
              setChoices: setMemberChoices,
            })}

            {isCouple &&
              renderOptions({
                name: "partnerSexualFantasies",
                subtitle: partnerLabel,
                choices: partnerChoices,
                setChoices: setPartnerChoices,
              })}

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving..." })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
